import { LoadingLayer } from '../../LoadingLayer';
import { useEffect, useState } from 'react';
import { SideMenuAdmin } from './SideMenuAdmin';
import './Admin.css';

const API_BASE = 'https://nt6dwl1xpc.execute-api.us-west-1.amazonaws.com/produccion';

const fetchCognitoUsers = async (setUsers) => {
  try {
    const response = await fetch(`${API_BASE}/admin/coordinators`, {
      headers: {
        'user-token': localStorage.getItem('id_token'),
      },
    });
    const data = await response.json();
    setUsers(data.users || []);
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

const manageUserGroup = async (username, groupName, action) => {
  const response = await fetch(`${API_BASE}/admin/coordinators/manage-group`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'user-token': localStorage.getItem('id_token'),
    },
    body: JSON.stringify({
      username,
      group_name: groupName,
      action,
    }),
  });
  return response.json();
};

const AVAILABLE_GROUPS = [
  'revisor',
  'revisor-advanced-materials-and-physics-of-nanostructures',
  'revisor-bionanotechnology',
  'revisor-nanophotonics',
  'revisor-physical-chemistry-of-nanomaterials-and-nanocatalysis',
  'revisor-tech-inovation-in-science',
  'coordinator',
  'admin',
];

export const AdminCoordinators = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('revisor');

  useEffect(() => {
    setIsLoading(true);
    setLoadingMessage('Loading users...');
    fetchCognitoUsers(setUsers).then(() => {
      setIsLoading(false);
    });
  }, []);

  const handleAddGroup = async (username) => {
    setUpdatingUsers((prev) => ({ ...prev, [username]: 'adding' }));
    try {
      await manageUserGroup(username, selectedGroup, 'add');
      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.username === username
            ? { ...u, groups: [...new Set([...u.groups, selectedGroup])] }
            : u,
        ),
      );
    } catch (error) {
      alert('Error adding user to group');
    }
    setUpdatingUsers((prev) => ({ ...prev, [username]: null }));
  };

  const handleRemoveGroup = async (username, groupName) => {
    setUpdatingUsers((prev) => ({ ...prev, [`${username}-${groupName}`]: 'removing' }));
    try {
      await manageUserGroup(username, groupName, 'remove');
      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.username === username
            ? { ...u, groups: u.groups.filter((g) => g !== groupName) }
            : u,
        ),
      );
    } catch (error) {
      alert('Error removing user from group');
    }
    setUpdatingUsers((prev) => ({ ...prev, [`${username}-${groupName}`]: null }));
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.groups || []).some((g) => g.toLowerCase().includes(q))
    );
  });

  return (
    <div className="snn-user-home-main-wrapper">
      <SideMenuAdmin />
      <div className="snn-abstract-submission-main-grid-wrapper">
        <LoadingLayer isLoading={isLoading} action={loadingMessage} />

        <section className="snn-expand-grid-4">
          <h1 className="snn-user-home-main-content-header">Manage Coordinators & Revisors</h1>

          {/* Search bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
              <span
                className="material-symbols-outlined"
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  fontSize: '20px',
                }}
              >
                search
              </span>
              <input
                type="text"
                placeholder="Search by email, username, name or group..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#94a3b8',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
              {filteredUsers.length} / {users.length} users
            </span>
          </div>

          {/* Group selector */}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: 600 }}>Group to assign:</label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: '13px',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            >
              {AVAILABLE_GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          {/* Users table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Email</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Username</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Status</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Groups</th>
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.username} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px 12px' }}>{user.email}</td>
                    <td style={{ padding: '8px 12px', fontSize: '12px', color: '#666' }}>
                      {user.username}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: user.enabled ? '#dcfce7' : '#fef3c7',
                          color: user.enabled ? '#166534' : '#92400e',
                        }}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {user.groups.length > 0 ? (
                          user.groups.map((group) => (
                            <span
                              key={group}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor:
                                  group === 'admin'
                                    ? '#fef2f2'
                                    : group === 'coordinator'
                                      ? '#fef3c7'
                                      : '#e0e7ff',
                                color:
                                  group === 'admin'
                                    ? '#991b1b'
                                    : group === 'coordinator'
                                      ? '#92400e'
                                      : '#3730a3',
                              }}
                            >
                              {group}
                              <button
                                onClick={() => handleRemoveGroup(user.username, group)}
                                disabled={updatingUsers[`${user.username}-${group}`] === 'removing'}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: '13px',
                                  lineHeight: 1,
                                }}
                                title={`Remove from ${group}`}
                              >
                                {updatingUsers[`${user.username}-${group}`] === 'removing'
                                  ? '...'
                                  : '×'}
                              </button>
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>No groups</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button
                        onClick={() => handleAddGroup(user.username)}
                        disabled={
                          updatingUsers[user.username] === 'adding' ||
                          user.groups.includes(selectedGroup)
                        }
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: user.groups.includes(selectedGroup)
                            ? '#22c55e'
                            : updatingUsers[user.username] === 'adding'
                              ? '#94a3b8'
                              : '#0b6ef6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: user.groups.includes(selectedGroup) ? 'not-allowed' : 'pointer',
                          opacity: user.groups.includes(selectedGroup) ? 0.5 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {user.groups.includes(selectedGroup)
                          ? `✓ In ${selectedGroup}`
                          : updatingUsers[user.username] === 'adding'
                            ? 'Adding...'
                            : `+ Add to ${selectedGroup}`}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

